import React, { useState, useEffect, useRef, useCallback } from 'react';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify } from '../../context/FeedbackContext';
import './ProductForm.css';
import './ConfigurableProductForm.css';
import { FaRegTrashAlt } from 'react-icons/fa';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

const EMPTY_FORM = {
  tenantCode: '',
  itemName: '',
  itemDesc: '',
  catReference: '',
  basePrice: '0.00',
  memberPrice: '0.00',
  festivalPrice: '0.00',
  seasonal: 'regular',
  status: 'Published',
  printerName: '',
  printerReference: '',
};

const newAttribute = () => ({
  id: Date.now(),
  name: 'Size',
  options: [
    { id: Date.now() + 1, label: 'Large', priceAdj: '3' },
    { id: Date.now() + 2, label: 'Medium', priceAdj: '2' },
    { id: Date.now() + 3, label: 'Small', priceAdj: '0' },
  ],
});

function ConfigurableProductForm({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [attributes, setAttributes] = useState([newAttribute()]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef(null);

  // 打印机列表
  const [prinList, setPrinList] = useState([]);

  const DESC_MAX = 50;

  const fetchCategories = useCallback(async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/catalog/categories/options?tenantReference=${selectedRestaurant.reference}`), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setCategories(data.data);
        if (data.data.length > 0) {
          setForm(prev => ({ ...prev, catReference: data.data[0].reference }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedRestaurant) {
      setForm(prev => ({ ...prev, tenantCode: selectedRestaurant.code }));
    }
  }, [selectedRestaurant]);

  // 查询打印机
  const getPrintIn = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`),
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.code === 200) {
        setPrinList(data.data);
      }
    } catch (err) {
      notify(err, 'error');
      console.error("Failed to fetch printers", err);
    }
  };

  useEffect(() => {
    getPrintIn(selectedRestaurant?.reference);
  }, [selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // 打印机选择
  const handleFormChangePrint = (e) => {
    const selectedReference = e.target.value;
    const selectedPrinter = prinList.find(p => p.reference === selectedReference);
    setForm(prev => ({
      ...prev,
      printerReference: selectedReference,
      printerName: selectedPrinter?.printerName || '',
    }));
    setFormErrors(prev => ({ ...prev, printerReference: undefined }));
  };

  // 选中分类后自动关联该分类的打印机
  useEffect(() => {
    if (!form.catReference || prinList.length === 0) {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
      return;
    }
    const selectedCat = categories.find(c => c.reference === form.catReference);
    if (selectedCat?.printerReference) {
      const matchedPrinter = prinList.find(p => p.reference === selectedCat.printerReference);
      if (matchedPrinter) {
        setForm(prev => ({
          ...prev,
          printerReference: matchedPrinter.reference,
          printerName: matchedPrinter.printerName,
        }));
      }
    } else {
      setForm(prev => ({ ...prev, printerReference: '', printerName: '' }));
    }
  }, [form.catReference, categories, prinList]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const errors = {};
    if (!form.itemName.trim()) errors.itemName = 'Product name is required.';
    if (!form.itemDesc.trim()) errors.itemDesc = 'Product description is required.';
    else if (form.itemDesc.trim().length > DESC_MAX) errors.itemDesc = `Description cannot exceed ${DESC_MAX} characters.`;
    if (!form.printerReference) errors.printerReference = 'Please select a printer.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  const addAttribute = () => {
    setAttributes(prev => [...prev, newAttribute()]);
  };

  const updateAttributeName = (attrId, name) => {
    setAttributes(prev => prev.map(a => a.id === attrId ? { ...a, name } : a));
  };

  const addOption = (attrId) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId
        ? { ...a, options: [...a.options, { id: Date.now(), label: '', priceAdj: '0' }] }
        : a
    ));
  };

  const updateOption = (attrId, optId, field, value) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId
        ? { ...a, options: a.options.map(o => o.id === optId ? { ...o, [field]: value } : o) }
        : a
    ));
  };

  const removeAttribute = (attrId) => {
    setAttributes(prev => prev.filter(a => a.id !== attrId));
  };

  const removeOption = (attrId, optId) => {
    setAttributes(prev => prev.map(a =>
      a.id === attrId ? { ...a, options: a.options.filter(o => o.id !== optId) } : a
    ));
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        tenantReference: selectedRestaurant?.reference,
        tenantCode: form.tenantCode,
        catReference: form.catReference,
        itemName: form.itemName.trim(),
        itemDesc: form.itemDesc.trim(),
        basePrice: parseFloat(form.basePrice) || 0,
        memberPrice: parseFloat(form.memberPrice) || 0,
        festivalPrice: parseFloat(form.festivalPrice) || 0,
        seasonal: form.seasonal === 'seasonal' ? 1 : 0,
        status: form.status === 'Published' ? 1 : 0,
        printerReference: form.printerReference || null,
        attributes: attributes.map((a, ai) => ({
          attName: a.name,
          position: ai + 1,
          options: a.options.map((o, oi) => ({
            option: o.label,
            priceAdjustment: parseFloat(o.priceAdj) || 0,
            position: oi + 1,
          })),
        })),
      };

      const formData = new FormData();
      formData.append('configurableProduct', JSON.stringify(payload));
      if (imageFile) formData.append('image', imageFile);

      const res = await fetch(buildApiUrl('/product/configurable/add'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.code === 200) {
        notify('Product added successfully!', 'success');
        onNavigate('Product');
      } else {
        notify('Failed to add product: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Error adding product:', err);
      notify('Failed to add product: ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onNavigate('Product');
  };

  const hasChanges = form.itemName !== '' || form.itemDesc !== '' || imageFile !== null;

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="pf-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li key={item} className={item === 'Product' ? 'active' : ''} onClick={() => onNavigate(getRouteFromItem(item))}>
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>

      <main className="pf-main">
        <h1 className="pf-title">Add New Configurable Product</h1>

        <div className="pf-card">
          {/* Row: Product Name + Categories */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Product Name <span className="pf-required">*</span></label>
              <input
                className={`pf-input${formErrors.itemName ? ' pf-input-error' : ''}`}
                name="itemName"
                value={form.itemName}
                onChange={handleChange}
                placeholder="Enter product name"
              />
              {formErrors.itemName
                ? <span className="pf-field-error">{formErrors.itemName}</span>
                : <span className="pf-hint">A product name is required and recommended to be unique</span>
              }
            </div>
            <div className="pf-field">
              <label>Categories</label>
              <select className="pf-select" name="catReference" value={form.catReference} onChange={handleChange} disabled={categories.length === 0}>
                {categories.length === 0
                  ? <option value="" disabled style={{ color: '#aaa' }}>-- no categories added. --</option>
                  : categories.map(c => (
                      <option key={c.reference} value={c.reference}>{c.catName}</option>
                    ))}
              </select>
            </div>
          </div>

          {/* Product Description */}
          <div className="pf-field">
            <label>Product Description <span className="pf-required">*</span></label>
            <div className="pf-editor">
              <textarea
                className={`pf-textarea${formErrors.itemDesc ? ' pf-input-error' : ''}`}
                name="itemDesc"
                value={form.itemDesc}
                onChange={handleChange}
                placeholder="Provide a detailed description of your product"
                maxLength={DESC_MAX + 1}
              />
            </div>
            {formErrors.itemDesc && <span className="pf-field-error">{formErrors.itemDesc}</span>}
          </div>

          {/* Upload Image */}
          <div className="pf-field">
            <button type="button" className="pf-upload-btn" onClick={() => imageInputRef.current.click()}>
              Upload Product Images
            </button>
            {imageFile && <span className="pf-hint" style={{ marginTop: 8 }}>{imageFile.name}</span>}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </div>

          {/* Row: Base Price + Seasonal */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Base Price</label>
              <input className="pf-input" name="basePrice" type="number" step="0.01" min="0" value={form.basePrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Seasonal</label>
              <select className="pf-select" name="seasonal" value={form.seasonal} onChange={handleChange}>
                <option value="regular">regular</option>
                <option value="seasonal">seasonal</option>
              </select>
            </div>
          </div>

          {/* Membership Price + Festival Price */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Membership Price</label>
              <input className="pf-input" name="memberPrice" type="number" step="0.01" min="0" value={form.memberPrice} onChange={handleChange} />
            </div>
            <div className="pf-field">
              <label>Festival Price</label>
              <input className="pf-input" name="festivalPrice" type="number" step="0.01" min="0" value={form.festivalPrice} onChange={handleChange} />
            </div>
          </div>

          {/* Attributes */}
          {attributes.map(attr => (
            <div key={attr.id} className="cpf-variant-block">
              <div className="cpf-variant-header">
                <span className="cpf-variant-title">Individual Variant Rules</span>
                <div className="cpf-header-actions">
                  <button type="button" className="cpf-add-attr-btn" onClick={addAttribute}>+ Add attributes</button>
                  {attributes.length > 1 && (
                    <button type="button" className="cpf-delete-attr-btn" onClick={() => removeAttribute(attr.id)}><FaRegTrashAlt /></button>
                  )}
                </div>
              </div>
              <div className="cpf-variant-body">
                <div className="cpf-attr-name-row">
                  <span className="cpf-attr-name-label">Attribute Name:</span>
                  <input
                    className="cpf-attr-name-input"
                    value={attr.name}
                    onChange={e => updateAttributeName(attr.id, e.target.value)}
                  />
                </div>
                {attr.options.map(opt => (
                  <div key={opt.id} className="cpf-option-row">
                    <input
                      className="cpf-option-label"
                      value={opt.label}
                      onChange={e => updateOption(attr.id, opt.id, 'label', e.target.value)}
                      placeholder="Option"
                    />
                    <span className="cpf-price-adj-label">PRICE ADJUSTMENT</span>
                    <div className="cpf-price-adj-input-wrap">
                      <span>$</span>
                      <input
                        className="cpf-price-adj-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={opt.priceAdj}
                        onChange={e => updateOption(attr.id, opt.id, 'priceAdj', e.target.value)}
                      />
                    </div>
                    <button type="button" className="cpf-delete-opt-btn" onClick={() => removeOption(attr.id, opt.id)}>
                      <FaRegTrashAlt />
                    </button>
                  </div>
                ))}
                <div className="cpf-add-option-row">
                  <button type="button" className="cpf-add-option-btn" onClick={() => addOption(attr.id)}>+ Add More Option</button>
                </div>
              </div>
            </div>
          ))}

          {/* Status + Printer */}
          <div className="pf-row-2col">
            <div className="pf-field">
              <label>Status</label>
              <select className="pf-select" name="status" value={form.status} onChange={handleChange}>
                <option>Published</option>
                <option>Unpublished</option>
              </select>
            </div>
            <div className="pf-field">
              <label>Printer <span className="pf-required">*</span></label>
              <select
                className={`pf-select${formErrors.printerReference ? ' pf-input-error' : ''}`}
                name="printerReference"
                value={form.printerReference || ''}
                onChange={handleFormChangePrint}
              >
                <option value="">-- Select Printer --</option>
                {prinList.map((printer) => (
                  <option key={printer.id} value={printer.reference}>
                    {printer.printerName}
                  </option>
                ))}
              </select>
              {formErrors.printerReference && <span className="pf-field-error">{formErrors.printerReference}</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pf-actions">
          <button className="pf-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>Cancel</button>
          <button
            className={`pf-save-btn${hasChanges ? '' : ' pf-save-inactive'}`}
            onClick={handleSave}
            disabled={isSubmitting || !hasChanges}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default ConfigurableProductForm;
